import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add,
  Archive,
  CheckCircle,
  ContentCopy,
  Download,
  Lock,
  RateReview,
  Undo,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../../config/api';
import { useAuth } from '../../../contexts/AuthContext';
import { formatCurrencyWithSymbol } from '../../../utils/currencyUtils';
import BudgetSectionEditor from './BudgetSectionEditor';
import {
  calculateLabourSectionTotal,
  clearStaleExpenseOverride,
  deriveEquipmentLine,
  deriveExpenseLine,
  deriveLabourLine,
  deriveRevenueLine,
  sumLines,
} from './budgetCalculations';

const STATUS_ACTIONS = {
  draft: [
    { status: 'in_review', icon: <RateReview />, key: 'submit' },
    { status: 'archived', icon: <Archive />, key: 'archive' },
  ],
  in_review: [
    { status: 'draft', icon: <Undo />, key: 'returnToDraft' },
    { status: 'approved', icon: <CheckCircle />, key: 'approve' },
    { status: 'archived', icon: <Archive />, key: 'archive' },
  ],
  approved: [
    { status: 'locked', icon: <Lock />, key: 'lock' },
    { status: 'archived', icon: <Archive />, key: 'archive' },
  ],
  locked: [{ status: 'archived', icon: <Archive />, key: 'archive' }],
  archived: [],
};

const FREQUENCIES = ['monthly', 'semimonthly', 'quarterly', 'annual', 'custom'];
const percent = (value) => (
  value === null || value === undefined
    ? '—'
    : Number(value).toLocaleString(undefined, { style: 'percent', maximumFractionDigits: 2 })
);
const statusColor = (status) => ({
  draft: 'default',
  in_review: 'warning',
  approved: 'success',
  locked: 'info',
  archived: 'default',
}[status] || 'default');

const BudgetAdmin = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [budgets, setBudgets] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [budget, setBudget] = useState(null);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);
  const [history, setHistory] = useState([]);
  const [newBudget, setNewBudget] = useState({
    name: '',
    fiscal_year: new Date().getFullYear(),
    scenario: 'Baseline',
    currency: 'CAD',
  });
  const [cloneForm, setCloneForm] = useState({ name: '', scenario: '' });
  const [labourSettings, setLabourSettings] = useState({});
  const [labourMonths, setLabourMonths] = useState([]);

  const readOnly = budget ? !['draft', 'in_review'].includes(budget.status) : true;

  const requestJson = async (endpoint, options, fallbackKey) => {
    const response = await makeApiCall(endpoint, options);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || t(fallbackKey));
    return data;
  };

  const loadBudgets = async (preferredId) => {
    const data = await requestJson('/api/admin/budgets', undefined, 'budget.errors.load');
    setBudgets(data);
    const nextId = preferredId || selectedId || data[0]?.id || '';
    setSelectedId(nextId);
    return nextId;
  };

  const loadBudget = async (id) => {
    if (!id) {
      setBudget(null);
      return;
    }
    const data = await requestJson(`/api/admin/budgets/${id}`, undefined, 'budget.errors.load');
    setBudget(data);
    setLabourSettings(data.labour_settings || {});
    setLabourMonths(data.labour_months || []);
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        setLoading(true);
        setError('');
        const id = await loadBudgets();
        if (id) await loadBudget(id);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId || String(selectedId) === String(budget?.id)) return;
    const changeBudget = async () => {
      try {
        setLoading(true);
        setError('');
        await loadBudget(selectedId);
        setTab(0);
        setHistory([]);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    changeBudget();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  useEffect(() => {
    if (tab !== 8 || !budget?.id) return;
    const loadHistory = async () => {
      try {
        const data = await requestJson(
          `/api/admin/budgets/${budget.id}/audit`,
          undefined,
          'budget.errors.history'
        );
        setHistory(data);
      } catch (err) {
        setError(err.message);
      }
    };
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, budget?.id]);

  const saveSection = async (section, body) => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const data = await requestJson(
        `/api/admin/budgets/${budget.id}/sections/${section}`,
        { method: 'PUT', body: JSON.stringify(body) },
        'budget.errors.save'
      );
      setBudget(data);
      setLabourSettings(data.labour_settings || {});
      setLabourMonths(data.labour_months || []);
      setSuccess(t('budget.messages.saved'));
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const createBudget = async () => {
    try {
      setSaving(true);
      setError('');
      const data = await requestJson(
        '/api/admin/budgets',
        { method: 'POST', body: JSON.stringify(newBudget) },
        'budget.errors.create'
      );
      setCreateOpen(false);
      setBudget(data);
      setSelectedId(data.id);
      await loadBudgets(data.id);
      setSuccess(t('budget.messages.created'));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const cloneBudget = async () => {
    try {
      setSaving(true);
      setError('');
      const data = await requestJson(
        `/api/admin/budgets/${budget.id}/clone`,
        { method: 'POST', body: JSON.stringify(cloneForm) },
        'budget.errors.clone'
      );
      setCloneOpen(false);
      setBudget(data);
      setSelectedId(data.id);
      await loadBudgets(data.id);
      setSuccess(t('budget.messages.cloned'));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async () => {
    if (!statusTarget) return;
    try {
      setSaving(true);
      setError('');
      const data = await requestJson(
        `/api/admin/budgets/${budget.id}/status`,
        { method: 'POST', body: JSON.stringify({ status: statusTarget.status }) },
        'budget.errors.status'
      );
      setBudget(data);
      setStatusTarget(null);
      await loadBudgets(data.id);
      setSuccess(t('budget.messages.statusChanged'));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const exportBudget = async () => {
    try {
      setSaving(true);
      setError('');
      const locale = i18n.resolvedLanguage || i18n.language || 'en';
      const response = await makeApiCall(
        `/api/admin/budgets/${budget.id}/export?locale=${encodeURIComponent(locale)}`
      );
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || t('budget.errors.export'));
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${budget.fiscal_year}-${budget.scenario}-v${budget.version}-budget.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    'overview', 'revenue', 'labour', 'equipment', 'materials',
    'subcontracting', 'overhead', 'pnl', 'history',
  ];

  const revenueFields = [
    { key: 'division', labelKey: 'budget.fields.division', required: true, sm: 4 },
    { key: 'season', labelKey: 'budget.fields.season', sm: 4 },
    { key: 'previous_amount', labelKey: 'budget.fields.previousAmount', type: 'number', min: 0, step: 0.01, sm: 4 },
    { key: 'forecast_amount', labelKey: 'budget.fields.forecastAmount', type: 'number', min: 0, step: 0.01, sm: 4 },
    { key: 'variance', labelKey: 'budget.fields.variance', readOnly: true, format: 'percent', sm: 4 },
    { key: 'comments', labelKey: 'budget.fields.comments', type: 'textarea', sm: 12 },
  ];
  const labourFields = [
    { key: 'employee_name', labelKey: 'budget.fields.employeeName', required: true, sm: 4 },
    {
      key: 'labour_group',
      labelKey: 'budget.fields.labourGroup',
      type: 'select',
      required: true,
      sm: 4,
      options: ['field_salary', 'field_hourly', 'overhead_salary', 'overhead_hourly']
        .map((value) => ({ value, labelKey: `budget.labourGroups.${value}` })),
    },
    { key: 'season', labelKey: 'budget.fields.season', sm: 4 },
    { key: 'annual_hours', labelKey: 'budget.fields.annualHours', type: 'number', min: 0, step: 0.25, sm: 4 },
    { key: 'hourly_rate', labelKey: 'budget.fields.hourlyRate', type: 'number', min: 0, step: 0.01, sm: 4 },
    { key: 'annual_salary', labelKey: 'budget.fields.annualSalary', type: 'number', min: 0, step: 0.01, sm: 4 },
    { key: 'bonus_benefit', labelKey: 'budget.fields.bonusBenefit', type: 'number', step: 0.01, sm: 4 },
    { key: 'allocation_pct', labelKey: 'budget.fields.allocation', type: 'number', min: 0, max: 1, step: 0.01, sm: 4 },
    { key: 'headcount', labelKey: 'budget.fields.headcount', type: 'number', min: 0, step: 1, sm: 4 },
    { key: 'budgeted_amount', labelKey: 'budget.fields.annualBudget', readOnly: true, format: 'currency', sm: 4 },
    { key: 'comments', labelKey: 'budget.fields.comments', type: 'textarea', sm: 8 },
  ];
  const equipmentFields = [
    { key: 'description', labelKey: 'budget.fields.description', required: true, sm: 4 },
    {
      key: 'equipment_group',
      labelKey: 'budget.fields.equipmentGroup',
      type: 'select',
      required: true,
      sm: 4,
      options: ['equipment', 'vehicle', 'operating', 'rental']
        .map((value) => ({ value, labelKey: `budget.equipmentGroups.${value}` })),
    },
    { key: 'season', labelKey: 'budget.fields.season', sm: 4 },
    { key: 'quantity', labelKey: 'budget.fields.quantity', type: 'number', min: 0, step: 1, sm: 4 },
    { key: 'periodic_amount', labelKey: 'budget.fields.periodicAmount', type: 'number', min: 0, step: 0.01, sm: 4 },
    {
      key: 'frequency',
      labelKey: 'budget.fields.frequency',
      type: 'select',
      sm: 4,
      options: FREQUENCIES.map((value) => ({ value, labelKey: `budget.frequencies.${value}` })),
    },
    {
      key: 'custom_multiplier',
      labelKey: 'budget.fields.customMultiplier',
      helperTextKey: 'budget.fieldHelp.customMultiplier',
      type: 'number',
      min: 0,
      step: 0.01,
      sm: 4,
      visibleWhen: (line) => line.frequency === 'custom',
    },
    { key: 'annual_amount', labelKey: 'budget.fields.annualBudget', readOnly: true, format: 'currency', sm: 4 },
    { key: 'comments', labelKey: 'budget.fields.comments', type: 'textarea', sm: 12 },
  ];
  const expenseFields = [
    { key: 'category', labelKey: 'budget.fields.category', required: true, sm: 4 },
    { key: 'vendor', labelKey: 'budget.fields.vendor', sm: 4 },
    { key: 'previous_amount', labelKey: 'budget.fields.previousAmount', type: 'number', step: 0.01, sm: 4 },
    { key: 'periodic_amount', labelKey: 'budget.fields.periodicAmount', type: 'number', min: 0, step: 0.01, sm: 4 },
    {
      key: 'frequency',
      labelKey: 'budget.fields.frequency',
      type: 'select',
      sm: 4,
      options: FREQUENCIES.map((value) => ({ value, labelKey: `budget.frequencies.${value}` })),
    },
    {
      key: 'custom_multiplier',
      labelKey: 'budget.fields.customMultiplier',
      helperTextKey: 'budget.fieldHelp.customMultiplier',
      type: 'number',
      min: 0,
      step: 0.01,
      sm: 4,
      visibleWhen: (line) => line.frequency === 'custom',
    },
    {
      key: 'annual_forecast_override',
      labelKey: 'budget.fields.forecastOverride',
      helperTextKey: 'budget.fieldHelp.forecastOverride',
      type: 'number',
      step: 0.01,
      sm: 4,
    },
    { key: 'allocation_pct', labelKey: 'budget.fields.allocation', type: 'number', min: 0, max: 1, step: 0.01, sm: 4 },
    { key: 'included_in_totals', labelKey: 'budget.fields.includeInTotals', type: 'switch', sm: 4 },
    { key: 'annual_forecast', labelKey: 'budget.fields.annualForecast', readOnly: true, format: 'currency', sm: 4 },
    { key: 'allocated_forecast', labelKey: 'budget.fields.allocatedForecast', readOnly: true, format: 'currency', sm: 4 },
    { key: 'variance', labelKey: 'budget.fields.variance', readOnly: true, format: 'percent', sm: 4 },
    { key: 'comments', labelKey: 'budget.fields.comments', type: 'textarea', sm: 12 },
  ];

  const overviewCards = useMemo(() => {
    if (!budget?.totals) return [];
    return [
      ['revenue', budget.totals.revenue_forecast, null],
      ['cogs', budget.totals.total_cogs, budget.totals.ratios.cogs],
      ['grossProfit', budget.totals.gross_profit, budget.totals.ratios.gross_profit],
      ['overhead', budget.totals.overhead_total, budget.totals.ratios.overhead],
      ['netProfit', budget.totals.net_profit, budget.totals.ratios.net_profit],
    ];
  }, [budget]);

  if (user?.user_type !== 'superadmin') {
    return <Alert severity="error">{t('budget.errors.superadminOnly')}</Alert>;
  }
  if (loading && !budget) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress aria-label={t('common.loading')} />
      </Box>
    );
  }

  const renderOverview = () => (
    <Box>
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="h6" component="h2">{t('budget.sections.overview')}</Typography>
        <Typography color="text.secondary">{t('budget.overviewDescription')}</Typography>
      </Paper>
      <Grid container spacing={2}>
        {overviewCards.map(([key, amount, ratio]) => (
          <Grid key={key} size={{ xs: 12, sm: 6, lg: 4 }}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography color="text.secondary">{t(`budget.summary.${key}`)}</Typography>
                <Typography variant="h5" component="p" sx={{ my: 1 }}>
                  {formatCurrencyWithSymbol(amount)}
                </Typography>
                {ratio !== null && (
                  <Typography variant="body2">{percent(ratio)}</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mt: 3 }}>
        <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
          {t('budget.benchmarks')}
        </Typography>
        <Grid container spacing={2}>
          {(budget.benchmarks || []).map((benchmark) => (
            <Grid key={benchmark.id} size={{ xs: 12, sm: 6, lg: 4 }}>
              <Box>
                <Typography variant="subtitle2">{benchmark.segment}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t(`budget.summary.${benchmark.metric}`)}: {percent(benchmark.minimum_pct)} – {percent(benchmark.maximum_pct)}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Box>
  );

  const labourAdditional = (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>{t('budget.labourSettings')}</Typography>
      <Grid container spacing={2}>
        {[
          ['field_burden_pct', 'fieldBurden', 0.01],
          ['overhead_burden_pct', 'overheadBurden', 0.01],
          ['weeks_per_month', 'weeksPerMonth', 0.25],
        ].map(([key, label, step]) => (
          <Grid key={key} size={{ xs: 12, sm: 4 }}>
            <TextField
              type="number"
              fullWidth
              label={t(`budget.fields.${label}`)}
              value={labourSettings[key] ?? ''}
              onChange={(event) => setLabourSettings((current) => ({
                ...current,
                [key]: event.target.value,
              }))}
              disabled={readOnly}
              inputProps={{ min: 0, step, 'aria-label': t(`budget.fields.${label}`) }}
            />
          </Grid>
        ))}
      </Grid>
      <Divider sx={{ my: 3 }} />
      <Typography variant="subtitle1" sx={{ mb: 2 }}>{t('budget.monthlyHours')}</Typography>
      <Grid container spacing={2}>
        {labourMonths.map((month, index) => (
          <Grid key={month.month_number} size={{ xs: 6, sm: 3 }}>
            <TextField
              type="number"
              fullWidth
              label={t(`budget.months.${month.month_number}`)}
              value={month.average_hours_per_week ?? ''}
              onChange={(event) => setLabourMonths((current) => current.map((item, itemIndex) => (
                itemIndex === index
                  ? { ...item, average_hours_per_week: event.target.value }
                  : item
              )))}
              disabled={readOnly}
              helperText={t('budget.hoursPerWeek')}
              inputProps={{ min: 0, step: 0.25, 'aria-label': t(`budget.months.${month.month_number}`) }}
            />
          </Grid>
        ))}
      </Grid>
    </Paper>
  );

  const renderPnl = () => {
    const lines = [
      ['revenue', budget.totals.revenue_forecast, 1],
      ['fieldLabour', budget.totals.field_total, budget.totals.ratios.field_labour],
      ['equipment', budget.totals.equipment_total, budget.totals.ratios.equipment],
      ['materials', budget.totals.materials, budget.totals.ratios.materials],
      ['subcontracting', budget.totals.subcontracting, budget.totals.ratios.subcontracting],
      ['cogs', budget.totals.total_cogs, budget.totals.ratios.cogs],
      ['grossProfit', budget.totals.gross_profit, budget.totals.ratios.gross_profit],
      ['overhead', budget.totals.overhead_total, budget.totals.ratios.overhead],
      ['netProfit', budget.totals.net_profit, budget.totals.ratios.net_profit],
    ];
    return (
      <Box>
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Typography variant="h6" component="h2">{t('budget.sections.pnl')}</Typography>
          <Typography color="text.secondary">{t('budget.pnlDescription')}</Typography>
        </Paper>
        <Stack spacing={1.5}>
          {lines.map(([key, amount, ratio]) => (
            <Paper key={key} variant="outlined" sx={{ p: 2 }}>
              <Grid container spacing={1} alignItems="center">
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography fontWeight={['revenue', 'cogs', 'grossProfit', 'netProfit'].includes(key) ? 700 : 400}>
                    {t(`budget.summary.${key}`)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 7, sm: 4 }}>
                  <Typography>{formatCurrencyWithSymbol(amount)}</Typography>
                </Grid>
                <Grid size={{ xs: 5, sm: 2 }}>
                  <Typography color="text.secondary">{percent(ratio)}</Typography>
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Stack>
      </Box>
    );
  };

  const renderHistory = () => (
    <Box>
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="h6" component="h2">{t('budget.sections.history')}</Typography>
        <Typography color="text.secondary">{t('budget.historyDescription')}</Typography>
      </Paper>
      <Stack spacing={2}>
        {history.length === 0 && (
          <Alert severity="info">{t('budget.noHistory')}</Alert>
        )}
        {history.map((event) => (
          <Paper key={event.id} variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
              <Box>
                <Typography fontWeight={600}>
                  {t(`budget.events.${event.event_type}`, { defaultValue: event.event_type })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {event.actor_name || t('budget.systemActor')}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {new Date(event.created_at).toLocaleString()}
              </Typography>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );

  const renderTab = () => {
    if (!budget) return <Alert severity="info">{t('budget.noBudgets')}</Alert>;
    switch (tab) {
      case 0:
        return renderOverview();
      case 1:
        return (
          <BudgetSectionEditor
            title={t('budget.sections.revenue')}
            description={t('budget.sectionDescriptions.revenue')}
            lines={budget.revenue}
            fields={revenueFields}
            emptyLine={{ division: '', season: '', previous_amount: 0, forecast_amount: 0, comments: '' }}
            readOnly={readOnly}
            saving={saving}
            deriveLine={deriveRevenueLine}
            getSectionTotal={(lines) => sumLines(lines, (line) => line.forecast_amount)}
            sectionTotalLabel={t('budget.sectionTotals.revenue')}
            onSave={(lines) => saveSection('revenue', { lines })}
          />
        );
      case 2:
        return (
          <BudgetSectionEditor
            title={t('budget.sections.labour')}
            description={t('budget.sectionDescriptions.labour')}
            lines={budget.labour}
            fields={labourFields}
            emptyLine={{
              employee_name: '', labour_group: 'field_hourly', annual_hours: 0,
              hourly_rate: 0, annual_salary: 0, bonus_benefit: 0,
              allocation_pct: 1, headcount: 1, comments: '',
            }}
            readOnly={readOnly}
            saving={saving}
            additionalContent={labourAdditional}
            deriveLine={deriveLabourLine}
            getSectionTotal={(lines) => calculateLabourSectionTotal(lines, labourSettings)}
            sectionTotalLabel={t('budget.sectionTotals.labour')}
            onSave={(lines) => saveSection('labour', {
              lines,
              settings: labourSettings,
              months: labourMonths,
            })}
          />
        );
      case 3:
        return (
          <BudgetSectionEditor
            title={t('budget.sections.equipment')}
            description={t('budget.sectionDescriptions.equipment')}
            lines={budget.equipment}
            fields={equipmentFields}
            emptyLine={{
              description: '', equipment_group: 'equipment', season: '', quantity: 1,
              periodic_amount: 0, frequency: 'monthly', custom_multiplier: '', comments: '',
            }}
            readOnly={readOnly}
            saving={saving}
            deriveLine={deriveEquipmentLine}
            getSectionTotal={(lines) => sumLines(lines, (line) => line.annual_amount)}
            sectionTotalLabel={t('budget.sectionTotals.equipment')}
            onSave={(lines) => saveSection('equipment', { lines })}
          />
        );
      case 4:
      case 5:
      case 6: {
        const section = ['material', 'subcontracting', 'overhead'][tab - 4];
        return (
          <BudgetSectionEditor
            title={t(`budget.sections.${section === 'material' ? 'materials' : section}`)}
            description={t(`budget.sectionDescriptions.${section}`)}
            lines={(budget.expenses || []).filter((line) => line.section === section)}
            fields={expenseFields}
            emptyLine={{
              category: section === 'overhead' ? t('budget.defaults.generalOverhead') : '',
              vendor: '', previous_amount: 0, periodic_amount: 0, frequency: 'annual',
              custom_multiplier: '', annual_forecast_override: '', allocation_pct: 1,
              included_in_totals: true, comments: '',
            }}
            readOnly={readOnly}
            saving={saving}
            deriveLine={deriveExpenseLine}
            onFieldChange={clearStaleExpenseOverride}
            getSectionTotal={(lines) => sumLines(
              lines.filter((line) => line.included_in_totals !== false),
              (line) => section === 'overhead'
                ? line.allocated_forecast
                : line.annual_forecast
            )}
            sectionTotalLabel={t(`budget.sectionTotals.${section}`)}
            onSave={(lines) => saveSection(section, { lines })}
          />
        );
      }
      case 7:
        return renderPnl();
      case 8:
        return renderHistory();
      default:
        return null;
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">{t('budget.title')}</Typography>
          <Typography color="text.secondary">{t('budget.subtitle')}</Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() => setCreateOpen(true)}
            aria-label={t('budget.actions.newBudget')}
            sx={{ minHeight: 44 }}
          >
            {t('budget.actions.newBudget')}
          </Button>
          {budget && (
            <>
              <Button
                variant="outlined"
                startIcon={<ContentCopy />}
                onClick={() => {
                  setCloneForm({ name: budget.name, scenario: budget.scenario });
                  setCloneOpen(true);
                }}
                aria-label={t('budget.actions.clone')}
                sx={{ minHeight: 44 }}
              >
                {t('budget.actions.clone')}
              </Button>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={exportBudget}
                disabled={saving}
                aria-label={t('budget.actions.export')}
                sx={{ minHeight: 44 }}
              >
                {t('budget.actions.export')}
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 5 }}>
            <TextField
              select
              fullWidth
              label={t('budget.fields.selectBudget')}
              value={selectedId}
              onChange={(event) => setSelectedId(event.target.value)}
              inputProps={{ 'aria-label': t('budget.fields.selectBudget') }}
            >
              {budgets.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.fiscal_year} · {item.scenario} · v{item.version} · {t(`budget.statuses.${item.status}`)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {budget && (
            <>
              <Grid size={{ xs: 12, md: 3 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={t(`budget.statuses.${budget.status}`)}
                    color={statusColor(budget.status)}
                    aria-label={t('budget.currentStatus', { status: t(`budget.statuses.${budget.status}`) })}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {budget.currency}
                  </Typography>
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
                  {(STATUS_ACTIONS[budget.status] || []).map((action) => (
                    <Button
                      key={action.status}
                      size="small"
                      variant={action.status === 'approved' ? 'contained' : 'outlined'}
                      color={action.status === 'archived' ? 'error' : 'primary'}
                      startIcon={action.icon}
                      onClick={() => setStatusTarget(action)}
                      aria-label={t(`budget.actions.${action.key}`)}
                      sx={{ minHeight: 44 }}
                    >
                      {t(`budget.actions.${action.key}`)}
                    </Button>
                  ))}
                </Stack>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {budget && (
        <Paper variant="outlined" sx={{ mb: 3 }}>
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            aria-label={t('budget.tabsAriaLabel')}
          >
            {tabs.map((key) => <Tab key={key} label={t(`budget.sections.${key}`)} />)}
          </Tabs>
        </Paper>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress aria-label={t('common.loading')} />
        </Box>
      ) : renderTab()}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('budget.dialogs.createTitle')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('budget.fields.name')}
                value={newBudget.name}
                onChange={(event) => setNewBudget((current) => ({ ...current, name: event.target.value }))}
                inputProps={{ 'aria-label': t('budget.fields.name') }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                type="number"
                fullWidth
                label={t('budget.fields.fiscalYear')}
                value={newBudget.fiscal_year}
                onChange={(event) => setNewBudget((current) => ({ ...current, fiscal_year: event.target.value }))}
                inputProps={{ min: 2000, max: 2200, 'aria-label': t('budget.fields.fiscalYear') }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('budget.fields.scenario')}
                value={newBudget.scenario}
                onChange={(event) => setNewBudget((current) => ({ ...current, scenario: event.target.value }))}
                inputProps={{ 'aria-label': t('budget.fields.scenario') }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                fullWidth
                label={t('budget.fields.currency')}
                value={newBudget.currency}
                onChange={(event) => setNewBudget((current) => ({ ...current, currency: event.target.value }))}
                inputProps={{ 'aria-label': t('budget.fields.currency') }}
              >
                <MenuItem value="CAD">CAD</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={createBudget} disabled={saving}>
            {t('budget.actions.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cloneOpen} onClose={() => setCloneOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('budget.dialogs.cloneTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label={t('budget.fields.name')}
              value={cloneForm.name}
              onChange={(event) => setCloneForm((current) => ({ ...current, name: event.target.value }))}
              inputProps={{ 'aria-label': t('budget.fields.name') }}
            />
            <TextField
              fullWidth
              label={t('budget.fields.scenario')}
              value={cloneForm.scenario}
              onChange={(event) => setCloneForm((current) => ({ ...current, scenario: event.target.value }))}
              inputProps={{ 'aria-label': t('budget.fields.scenario') }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={cloneBudget} disabled={saving}>
            {t('budget.actions.clone')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!statusTarget} onClose={() => setStatusTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>{statusTarget ? t(`budget.dialogs.${statusTarget.key}Title`) : ''}</DialogTitle>
        <DialogContent>
          <Typography>
            {statusTarget ? t(`budget.dialogs.${statusTarget.key}Description`) : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusTarget(null)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color={statusTarget?.status === 'archived' ? 'error' : 'primary'}
            onClick={changeStatus}
            disabled={saving}
          >
            {statusTarget ? t(`budget.actions.${statusTarget.key}`) : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BudgetAdmin;
